<template>
    <v-card>
        <v-card-title>정산정보상세</v-card-title>
        <v-card-text>
            <v-data-table :headers="headers" :items="items" disable-sort hide-default-footer>
                <template v-slot:item.createdAt="{ item }">
                    {{ item.createdAt.toDate() }}
                </template>
            </v-data-table>
            <v-row class="my-4 pa-4">
                <v-col>
                    <v-row v-if="manualItems.length">
                        <v-col cols="7" class="py-0"> 수동처리사유</v-col>
                        <v-col class="py-0"> 수동처리금액 </v-col>
                    </v-row>
                    <v-row v-for="(item, index) in manualItems" :key="index" class="mt-0" align="baseline">
                        <v-col cols="7" class="py-0"> <v-text-field v-model="item.manualComment" @input="item.save = true" hide-details :readonly="!isEditable" /> </v-col>
                        <v-col class="py-0">
                            <v-text-field v-model="item.manualAmount" @input="item.save = true" type="number" placeholder="예) -10000, 100000" suffix="원" hide-details :readonly="!isEditable" />
                        </v-col>
                        <v-col cols="1" class="py-0">
                            <v-row cols="auto" v-if="isEditable">
                                <v-col class="px-2" v-if="isEditable"> <v-icon @click="removeManual(item)" small color="red">mdi-minus-circle</v-icon> </v-col>
                                <v-col class="px-2" v-if="isEditable && item.save"> <v-icon @click="$emit('input', value)" small color="green">mdi-content-save</v-icon> </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                    <v-row justify="center" v-if="isEditable">
                        <v-col cols="auto" class="px-1">
                            <v-btn @click="addManual" small color="secondary">항목추가 <v-icon small class="ml-2">mdi-plus-circle</v-icon></v-btn>
                        </v-col>
                        <v-col v-if="manualToSave" cols="auto" class="px-1">
                            <v-btn @click="$emit('input', value)" small color="secondary">일괄저장 <v-icon small class="ml-2">mdi-content-save</v-icon></v-btn>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </v-card-text>
        <v-card-actions>
            <info-detail-summary :total="total" />
        </v-card-actions>
    </v-card>
</template>

<script>
import infoDetailSummary from "./info-detail-summary.vue";
export default {
    components: { infoDetailSummary },
    props: {
        value: {
            amounts: { type: Array },
            purchases: { type: Array },
            isDone: { type: Boolean },
        },
    },
    data() {
        return {
            headers: [
                { text: "구매일", value: "purchaseDate", width: 110 },
                { text: "상품명", value: "productName" },
                { text: "판매금액", value: "saleAmount", width: 120 },
                { text: "배송비 (도서산간포함)", value: "deliveryAmount", width: 160 },
                { text: "PG사수수료", value: "paymentCommission", width: 120 },
                { text: "플랫폼수수료", value: "defaultCommission", width: 120 },
                { text: "소계", value: "totalAmount", width: 120 },
            ],
            items: [],
            manualHeaders: [
                { text: "수동처리사유", value: "manualComment" },
                { text: "가감액", value: "manualAmount", width: 140 },
            ],
            dialog: false,
        };
    },
    computed: {
        isConsole() {
            return this.$store.state.scope.includes("console");
        },
        isEditable() {
            return this.isConsole && !this.value.isDone;
        },
        total() {
            return this.value.amounts.reduce(
                (total, amount) => ({
                    sale: total.sale + (amount.saleAmount || 0),
                    delivery: total.delivery + this.getDeliveryAmount(amount),
                    dCom: total.dCom + this.getDefaultCommission(amount),
                    pCom: total.pCom + this.getPaymentCommission(amount),
                    manual: total.manual + (+amount.manualAmount || 0),
                    sum: total.sum + this.getTotalAmount(amount) + (+amount.manualAmount || 0),
                }),
                { sale: 0, delivery: 0, dCom: 0, pCom: 0, manual: 0, sum: 0 }
            );
        },
        amounts() {
            return this.value.amounts.filter(({ manualAmount }) => manualAmount == undefined);
        },
        manualItems() {
            return this.value.amounts.filter(({ manualAmount }) => manualAmount !== undefined);
        },
        manualToSave() {
            return this.manualItems.some(({ save }) => save);
        },
        totalAmounts() {
            return [...this.amounts, ...this.manualItems];
        },
    },
    mounted() {
        this.init();
    },
    methods: {
        init() {
            this.items = this.amounts.map((amount) => {
                return {
                    purchaseDate: this.getPurchaseDate(amount),
                    productName: this.getProductName(amount),
                    saleAmount: this.getSaleAmount(amount),
                    deliveryAmount: this.getDeliveryAmount(amount, true),
                    defaultCommission: this.getDefaultCommission(amount, true),
                    paymentCommission: this.getPaymentCommission(amount, true),
                    totalAmount: this.getTotalAmount(amount, true),
                };
            });
        },
        getPurchase(_purchase) {
            return this.value.purchases.find(({ _id }) => _id == _purchase);
        },
        getPurchaseDate({ _purchase }) {
            return this.getPurchase(_purchase)?.createDate.toDate();
        },
        getProductName({ _purchase }) {
            return this.getPurchase(_purchase)?.product?.name;
        },
        getSaleAmount({ saleAmount }) {
            return saleAmount.format() + " 원";
        },
        getDeliveryAmount({ deliveryAmount, islandAmount }, withFormat = false) {
            const value = deliveryAmount + islandAmount || 0;
            return withFormat ? value.format() + " 원" : value;
        },
        getDefaultCommission(amount, withFormat = false) {
            const value = Math.round((amount.saleAmount + this.getDeliveryAmount(amount)) * (amount.defaultCommission * 0.01)) || 0;
            return withFormat ? value.format() + " 원" : value;
        },
        getPaymentCommission(amount, withFormat = false) {
            const value = Math.round((amount.saleAmount + this.getDeliveryAmount(amount)) * (amount.paymentCommission * 0.01)) || 0;
            return withFormat ? value.format() + " 원" : value;
        },
        getTotalAmount(amount, withFormat = false) {
            const value = amount.saleAmount + this.getDeliveryAmount(amount) - this.getDefaultCommission(amount) - this.getPaymentCommission(amount);
            return withFormat ? value.format() + " 원" : value;
        },
        addManual() {
            this.$emit("input", { ...this.value, amounts: [...this.totalAmounts, { manualAmount: null, manualComment: "" }] });
        },
        removeManual(item) {
            this.$emit("input", { ...this.value, amounts: [...this.totalAmounts.filter(({ _id }) => _id != item._id)] });
        },
    },
};
</script>

<style></style>
